import { api } from '@goaltree/backend/convex/_generated/api'
import type { PlanTerm } from '@goaltree/backend/convex/lib/planTerm'
import { useAction, useConvexAuth, useMutation, useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { posthog } from '@/lib/posthog'

// No-code: a static checkout link from Polar dashboard (simplest, no API key needed)
const POLAR_CHECKOUT_URL = import.meta.env.VITE_POLAR_CHECKOUT_URL as string | undefined
// API approach: create checkout sessions dynamically (needs POLAR_API_KEY on backend)
const POLAR_PRODUCT_ID = import.meta.env.VITE_POLAR_PRODUCT_ID as string | undefined

export function usePolar() {
  const { t } = useTranslation()
  const { isAuthenticated } = useConvexAuth()
  const currentUser = useQuery(api.auth.getCurrentUser, isAuthenticated ? {} : 'skip')
  const createCheckout = useAction(api.polar.createCheckout)
  const getPortalUrl = useAction(api.polar.getCustomerPortalUrl)
  const syncEmail = useMutation(api.preferences.syncEmail)

  const isConfigured = !!(POLAR_CHECKOUT_URL || POLAR_PRODUCT_ID)

  // Month keeps the original single-product flow; year and lifetime need their
  // Polar products set on the backend.
  async function checkout(plan: PlanTerm = 'month') {
    if (plan !== 'month') return checkoutPlan(plan)
    if (!isConfigured) {
      toast.error(t('upgrade.toastBillingUnconfigured'))
      return false
    }
    if (!isAuthenticated) {
      toast.error(t('upgrade.toastNotReady'))
      return false
    }

    try {
      posthog.capture('checkout_started', { provider: 'polar', plan })

      // Ensure email is stored so the webhook can look up the user
      await syncEmail({}).catch(() => {})

      // Prefer the simple checkout link — no API key required
      if (POLAR_CHECKOUT_URL) {
        const successUrl = encodeURIComponent(`${window.location.origin}/upgrade/success`)
        // Pre-fill email + pass convex_user_id through to webhook metadata
        const userId = currentUser?._id ?? ''
        const email = currentUser?.email ?? ''
        const name = currentUser?.name ?? ''
        const params = new URLSearchParams({
          'metadata[convex_user_id]': userId,
          success_url: decodeURIComponent(successUrl),
        })
        if (email) params.set('customer_email', email)
        if (name) params.set('customer_name', name)
        window.location.href = `${POLAR_CHECKOUT_URL}?${params.toString()}`
        return true
      }

      // Fall back to API-created checkout session
      const successUrl = `${window.location.origin}/upgrade/success`
      const { url } = await createCheckout({ productId: POLAR_PRODUCT_ID!, plan, successUrl })
      window.location.href = url
      return true
    } catch (err) {
      console.error('[polar] checkout error:', err)
      toast.error(t('upgrade.toastFailed'))
      return false
    }
  }

  async function checkoutPlan(plan: PlanTerm) {
    if (!isAuthenticated) {
      toast.error(t('upgrade.toastNotReady'))
      return false
    }
    try {
      posthog.capture('checkout_started', { provider: 'polar', plan })
      await syncEmail({}).catch(() => {})
      const { url } = await createCheckout({
        plan,
        successUrl: `${window.location.origin}/upgrade/success${plan === 'lifetime' ? '?plan=lifetime' : ''}`,
      })
      window.location.href = url
      return true
    } catch (err) {
      console.error('[polar] checkout error:', err)
      toast.error(t('upgrade.toastFailed'))
      return false
    }
  }

  async function getManagementURL(): Promise<string | null> {
    if (!isAuthenticated) return null
    try {
      return await getPortalUrl({})
    } catch {
      return null
    }
  }

  return { checkout, getManagementURL, isConfigured }
}
